import React, { useState, useEffect, useCallback, useRef } from 'react';
import './DPAnalyzer.css';
import TopBar from './TopBar';
import DPChat from './DPChat';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import AnalysisView from './AnalysisView';
import DocViewerModal from './DocViewerModal';

import API_BASE_URL from '../config/api';

const DPAnalyzer = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  // Document viewer: {url, filename, page}. The presigned URL is cached per
  // analysis (valid 1h) so repeated citation clicks don't re-hit the API.
  const [viewer, setViewer] = useState(null);
  const docUrlCache = useRef({});
  // Problemkarta (annotated risk map): presigned URL fetched lazily when a
  // completed analysis has one, cached per analysis like the document URL.
  const [problemkartaUrl, setProblemkartaUrl] = useState(null);
  const problemkartaCache = useRef({});
  // Beslutsbilder (5-slide PowerPoint for decision-makers), built server-side
  // after the analysis completes: {id, status: none|processing|completed|error, error}
  const [slides, setSlides] = useState(null);
  // The same deck in English, built on demand: same shape as `slides`
  const [slidesEn, setSlidesEn] = useState(null);
  // History: one completed row at a time is expanded to show its actions
  const [expandedId, setExpandedId] = useState(null);
  const currentId = currentAnalysis?.analysisId;
  const currentStatus = currentAnalysis?.status;
  // A re-run (asked for in the chat) replaces a completed analysis: the old
  // one stays shown and usable until completedAt moves on.
  const currentCompletedAt = currentAnalysis?.completedAt;
  const rerunning = currentAnalysis?.rerunStatus === 'processing';
  const slidesId = slides?.id;
  const slidesStatus = slides?.status;
  const slidesEnId = slidesEn?.id;
  const slidesEnStatus = slidesEn?.status;

  // Load user's analyses on mount
  useEffect(() => {
    fetchAnalyses();
  }, []);

  useEffect(() => {
    const a = currentAnalysis;
    if (!a || a.status !== 'completed' || !a.problemkartaKey) {
      setProblemkartaUrl(null);
      return;
    }
    // Keyed on completedAt too: a re-run redraws the map under the same S3 key.
    const id = a.analysisId;
    const cacheKey = `${id}:${a.completedAt}`;
    if (problemkartaCache.current[cacheKey]) {
      setProblemkartaUrl(problemkartaCache.current[cacheKey]);
      return;
    }
    let cancelled = false;
    fetch(`${API_BASE_URL}/dp-analysis/${id}/document?file=problemkarta`, {
      credentials: 'include'
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data && data.url) {
          problemkartaCache.current[cacheKey] = data.url;
          setProblemkartaUrl(data.url);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentAnalysis]);

  // Status only - the download URL is fetched on click (?download=1), so
  // polling is not logged as a download.
  const fetchSlides = useCallback(async (id, lang = 'sv') => {
    try {
      const res = await fetch(`${API_BASE_URL}/dp-analysis/${id}/slides?lang=${lang}`, {
        credentials: 'include'
      });
      if (!res.ok) return;
      const data = await res.json();
      (lang === 'en' ? setSlidesEn : setSlides)(
        { id, status: data.status, error: data.error, note: data.note });
    } catch (err) {
      console.error('Failed to fetch slides status:', err);
    }
  }, []);

  // Look up the deck whenever a completed analysis is shown. Keyed on id,
  // status and completedAt (a finished re-run has started a new deck), not the
  // analysis object, which is replaced on every status poll.
  useEffect(() => {
    if (!currentId || currentStatus !== 'completed') {
      setSlides(null);
      setSlidesEn(null);
      return;
    }
    fetchSlides(currentId);
    fetchSlides(currentId, 'en');
  }, [currentId, currentStatus, currentCompletedAt, fetchSlides]);

  // The deck takes ~1.5 minutes; poll while it is being built.
  useEffect(() => {
    if (slidesStatus !== 'processing' || !slidesId) return undefined;
    const interval = setInterval(() => fetchSlides(slidesId), 5000);
    return () => clearInterval(interval);
  }, [slidesId, slidesStatus, fetchSlides]);

  useEffect(() => {
    if (slidesEnStatus !== 'processing' || !slidesEnId) return undefined;
    const interval = setInterval(() => fetchSlides(slidesEnId, 'en'), 5000);
    return () => clearInterval(interval);
  }, [slidesEnId, slidesEnStatus, fetchSlides]);

  const downloadSlides = async (id, lang = 'sv') => {
    try {
      const res = await fetch(`${API_BASE_URL}/dp-analysis/${id}/slides?download=1&lang=${lang}`, {
        credentials: 'include'
      });
      const data = res.ok ? await res.json() : null;
      if (data?.downloadUrl) {
        window.open(data.downloadUrl, '_blank');
      } else {
        setError('Kunde inte ladda ner beslutsbilderna');
      }
    } catch (err) {
      setError('Kunde inte ladda ner beslutsbilderna');
    }
  };

  const handleSlides = async (id, lang = 'sv') => {
    const set = lang === 'en' ? setSlidesEn : setSlides;
    if ((lang === 'en' ? slidesEnStatus : slidesStatus) === 'completed') {
      await downloadSlides(id, lang);
      return;
    }
    // Not built yet (older analysis, or English on demand) or failed: start building.
    try {
      const res = await fetch(`${API_BASE_URL}/dp-analysis/${id}/slides?lang=${lang}`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = res.ok ? await res.json() : null;
      set(data
        ? { id, status: data.status }
        : { id, status: 'error', error: 'Beslutsbilderna kunde inte startas.' });
    } catch (err) {
      set({ id, status: 'error', error: 'Beslutsbilderna kunde inte startas.' });
    }
  };

  // Poll for status updates when analysis is processing.
  // A two-stage analysis of a large bundle can run for several minutes, so the
  // interval backs off after the first minute rather than issuing hundreds of
  // authenticated requests. Elapsed time comes from the analysis's own startedAt:
  // this effect re-runs on every poll, so a locally captured start time would reset
  // each tick and the backoff would never engage.
  useEffect(() => {
    const rerun = currentAnalysis?.rerunStatus === 'processing';
    if (currentAnalysis?.status === 'processing' || rerun) {
      const startedAt = Date.parse(
        `${rerun ? currentAnalysis.rerunStartedAt : currentAnalysis.startedAt}Z`);
      const elapsed = Number.isNaN(startedAt) ? 0 : Date.now() - startedAt;
      const interval = setInterval(() => {
        checkAnalysisStatus(currentAnalysis.analysisId);
      }, elapsed > 60000 ? 6000 : 3000);
      return () => clearInterval(interval);
    }
  }, [currentAnalysis]);

  const fetchAnalyses = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/dp-analysis`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAnalyses(data.analyses || []);
      }
    } catch (err) {
      console.error('Failed to fetch analyses:', err);
    }
  };

  const checkAnalysisStatus = async (analysisId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/dp-analysis/${analysisId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentAnalysis(data);

        // Not on every poll of a re-run - the analysis is 'completed' throughout
        if ((data.status === 'completed' || data.status === 'error')
            && data.rerunStatus !== 'processing') {
          fetchAnalyses();
        }
      }
    } catch (err) {
      console.error('Failed to check status:', err);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelect = (selectedFile) => {
    const validExtensions = ['.pdf', '.zip'];
    const fileName = selectedFile.name.toLowerCase();
    const isValid = validExtensions.some(ext => fileName.endsWith(ext));

    if (!isValid) {
      setError('Endast PDF- och ZIP-filer accepteras');
      return;
    }

    // Check file size (max 50MB)
    const maxSize = 50 * 1024 * 1024;
    if (selectedFile.size > maxSize) {
      setError('Filen är för stor. Maximal storlek är 50MB.');
      return;
    }

    setFile(selectedFile);
    setError(null);
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setError(null);
    setUploadProgress('Förbereder uppladdning...');

    try {
      // Step 1: Get presigned URL from API
      const contentType = file.name.toLowerCase().endsWith('.zip')
        ? 'application/zip'
        : 'application/pdf';

      const uploadResponse = await fetch(`${API_BASE_URL}/dp-analysis/upload`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          filename: file.name,
          contentType: contentType
        })
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        throw new Error(errorData.error || 'Kunde inte förbereda uppladdning');
      }

      const { analysisId, uploadUrl } = await uploadResponse.json();
      setUploadProgress('Laddar upp fil till servern...');

      // Step 2: Upload file directly to S3 using presigned URL
      const s3Response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': contentType
        },
        body: file
      });

      if (!s3Response.ok) {
        throw new Error('Uppladdning till lagringsplatsen misslyckades');
      }

      setUploadProgress('Startar analys...');

      // Step 3: Start the analysis
      const startResponse = await fetch(`${API_BASE_URL}/dp-analysis/start`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          analysisId: analysisId
        })
      });

      if (!startResponse.ok) {
        const errorData = await startResponse.json();
        throw new Error(errorData.error || 'Kunde inte starta analys');
      }

      const startData = await startResponse.json();

      setCurrentAnalysis({
        analysisId: startData.analysisId,
        status: 'processing',
        originalFilename: file.name,
        timestamp: new Date().toISOString()
      });
      setFile(null);
      setUploadProgress('');

    } catch (err) {
      setError(err.message);
      setUploadProgress('');
    } finally {
      setUploading(false);
    }
  };

  // Open the document viewer at a page. Presigned URL cached per analysis.
  const openDocPage = async (analysisId, filename, page) => {
    try {
      let cached = docUrlCache.current[analysisId];
      if (!cached) {
        const res = await fetch(`${API_BASE_URL}/dp-analysis/${analysisId}/document`, {
          credentials: 'include'
        });
        if (!res.ok) throw new Error('document url failed');
        cached = await res.json();
        docUrlCache.current[analysisId] = cached;
      }
      // Force a state change even when re-opening the same page so the
      // viewer's scroll effect re-triggers.
      setViewer(null);
      setTimeout(() => setViewer({
        url: cached.url,
        filename: cached.filename || filename || 'document.pdf',
        page: page || 1,
      }), 0);
    } catch (err) {
      console.error(err);
      setError('Kunde inte öppna dokumentet');
    }
  };

  // Load a past analysis (incl. its result) into the main panel.
  const openAnalysis = async (analysisId) => {
    setShowAnalysis(false);
    await checkAnalysisStatus(analysisId);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const downloadWord = async (analysisId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/dp-analysis/${analysisId}/word`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Kunde inte hämta nedladdningslänk');
      }

      const data = await response.json();
      if (data.downloadUrl) {
        window.open(data.downloadUrl, '_blank');
      }
    } catch (err) {
      setError('Kunde inte ladda ner rapport');
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending_upload': return 'Väntar på uppladdning';
      case 'processing': return 'Analyserar...';
      case 'completed': return 'Klar';
      case 'error': return 'Fel';
      default: return status;
    }
  };

  // The panel shows any opened analysis, finished ones included - its heading
  // must follow the status, not always say "Pågående".
  const getPanelTitle = (status) => {
    switch (status) {
      case 'pending_upload': return 'Laddar upp';
      case 'processing': return 'Pågående analys';
      case 'completed': return 'Klar analys';
      case 'error': return 'Analysen misslyckades';
      default: return 'Analys';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'pending_upload': return 'status-pending';
      case 'processing': return 'status-processing';
      case 'completed': return 'status-completed';
      case 'error': return 'status-error';
      default: return '';
    }
  };

  const getDisplayFilenames = (analysis) => {
    if (analysis.originalFilenames && analysis.originalFilenames.length > 0) {
      return analysis.originalFilenames.join(', ');
    }
    if (analysis.originalFilename) {
      return analysis.originalFilename;
    }
    return 'Okänd fil';
  };

  return (
    <>
      <TopBar />
      <div className="dp-analyzer ut-page">
      <div className="dp-analyzer-header">
        <div className="eyebrow">Hälsosam Urbanism</div>
        <h1>DP-Analys</h1>
        <p className="subtitle">
          Ladda upp detaljplaner och byggnadsdokumentation (PDF eller ZIP) för analys av hälsofrämjande livsmiljöer
        </p>
      </div>

      {/* Upload Area */}
      <div
        className={`upload-area r1 ${dragActive ? 'drag-active' : ''} ${file ? 'has-file' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {file ? (
          <div className="file-selected">
            <span className="file-glyph" aria-hidden="true">
              {file.name.toLowerCase().endsWith('.pdf') ? 'PDF' : 'ZIP'}
            </span>
            <div className="file-info">
              <span className="file-name">{file.name}</span>
              <span className="file-size">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </span>
            </div>
            <button onClick={() => setFile(null)} className="remove-btn" aria-label="Ta bort" data-tip="dp.remove">
              ×
            </button>
          </div>
        ) : (
          <div className="upload-prompt">
            <span className="upload-glyph" aria-hidden="true"></span>
            <p>Dra och släpp PDF/ZIP här</p>
            <span className="or-text">eller</span>
            <label className="file-input-label" data-tip="dp.chooseFile">
              Välj fil
              <input
                type="file"
                accept=".pdf,.zip"
                onChange={(e) => handleFileSelect(e.target.files[0])}
                hidden
              />
            </label>
            <p className="file-hint">
              Max 50 MB. Stödjer PDF och ZIP-filer. Ta med plankartan (ZIP med planbeskrivning
              och plankarta) för kartorna i beslutsbilderna.
            </p>
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      <button
        onClick={handleUpload}
        disabled={!file || uploading}
        className="upload-btn"
        data-tip="dp.start"
      >
        {uploading ? (
          <>
            <span className="btn-spinner"></span>
            {uploadProgress || 'Laddar upp...'}
          </>
        ) : (
          'Starta analys'
        )}
      </button>

      {/* Current Analysis Status */}
      {currentAnalysis && (
        <div className={`analysis-status r3 ${getStatusClass(currentAnalysis.status)}`}>
          <h3>{getPanelTitle(currentAnalysis.status)}</h3>
          <div className="status-details">
            <p>
              <strong>Fil:</strong> {getDisplayFilenames(currentAnalysis)}
            </p>
            <p>
              <strong>Status:</strong>{' '}
              <span className={`status-badge ${getStatusClass(currentAnalysis.status)}`}>
                {getStatusText(currentAnalysis.status)}
              </span>
            </p>
          </div>

          {currentAnalysis.status === 'processing' && (
            <div className="processing-indicator">
              <div className="spinner"></div>
              {/* Large documents are analysed in parts; the backend reports how many
                  are done. Absent on the single-call path, hence the fallback. */}
              <p>{currentAnalysis.progress?.text || 'Analyserar dokumentet med AI...'}</p>
            </div>
          )}

          {currentAnalysis.status === 'completed' && (
            <>
              {rerunning && (
                <div className="processing-indicator rerun-indicator">
                  <div className="spinner"></div>
                  <p>
                    Analysen körs om med rättelserna
                    {currentAnalysis.progress?.text ? ` – ${currentAnalysis.progress.text}` : '…'}
                    {' '}Den nuvarande analysen visas tills den nya är klar, och
                    beslutsbilderna skapas om efteråt.
                  </p>
                </div>
              )}
              {currentAnalysis.rerunStatus === 'error' && (
                <p className="slides-error">{currentAnalysis.rerunError}</p>
              )}
              <div className="download-buttons">
                <button
                  onClick={() => downloadWord(currentAnalysis.analysisId)}
                  className="download-btn word"
                  data-tip="dp.word"
                >
                  📝 Ladda ner Word
                </button>
                {(() => {
                  // Ignore a state left over from a previously shown analysis
                  const st = slidesId === currentAnalysis.analysisId ? slidesStatus : null;
                  return (
                    <button
                      onClick={() => handleSlides(currentAnalysis.analysisId)}
                      className="download-btn slides"
                      disabled={!st || st === 'processing' || (rerunning && st !== 'completed')}
                      data-tip={rerunning && st !== 'completed' ? 'dp.slidesRerun'
                        : st === 'completed' ? 'dp.slides'
                        : st === 'outdated' ? 'dp.slidesOutdated'
                        : st === 'processing' ? 'dp.slidesBusy' : 'dp.slidesCreate'}
                    >
                      {st === 'processing' ? (
                        <><span className="btn-spinner" aria-hidden="true"></span>Skapar beslutsbilder…</>
                      ) : st === 'completed' ? '📊 Ladda ner beslutsbilder'
                        : st === 'outdated' ? '📊 Uppdatera beslutsbilder'
                        : st === 'error' ? '📊 Skapa beslutsbilder igen'
                        : st === 'none' ? '📊 Skapa beslutsbilder'
                        : '📊 Beslutsbilder'}
                    </button>
                  );
                })()}
                {(() => {
                  // The English deck is built on demand, after the Swedish one:
                  // both share the site analysis, which must not run twice.
                  const st = slidesEnId === currentAnalysis.analysisId ? slidesEnStatus : null;
                  const sv = slidesId === currentAnalysis.analysisId ? slidesStatus : null;
                  const waiting = sv === 'processing';
                  return (
                    <button
                      onClick={() => handleSlides(currentAnalysis.analysisId, 'en')}
                      className="download-btn slides"
                      disabled={!st || st === 'processing' || waiting
                        || (rerunning && st !== 'completed')}
                      data-tip={rerunning && st !== 'completed' ? 'dp.slidesRerun'
                        : st === 'completed' ? 'dp.slidesEn'
                        : st === 'outdated' ? 'dp.slidesEnOutdated'
                        : st === 'processing' ? 'dp.slidesEnBusy'
                        : waiting ? 'dp.slidesEnWait' : 'dp.slidesEnCreate'}
                    >
                      {st === 'processing' ? (
                        <><span className="btn-spinner" aria-hidden="true"></span>Creating English slides…</>
                      ) : st === 'completed' ? '📊 English slides'
                        : st === 'outdated' ? '📊 Update English slides'
                        : st === 'error' ? '📊 Create English slides again'
                        : '📊 Create English slides'}
                    </button>
                  );
                })()}
                <button
                  onClick={() => openDocPage(currentAnalysis.analysisId,
                    getDisplayFilenames(currentAnalysis), 1)}
                  className="download-btn"
                  data-tip="dp.viewDoc"
                >
                  📄 Visa dokument
                </button>
                {currentAnalysis.result && (
                  <button
                    onClick={() => setShowAnalysis((v) => !v)}
                    className="download-btn"
                    data-tip={showAnalysis ? 'dp.hideAnalysis' : 'dp.showAnalysis'}
                  >
                    {showAnalysis ? '▾ Dölj analys' : '▸ Visa analys'}
                  </button>
                )}
              </div>
              {slidesId === currentAnalysis.analysisId && slidesStatus === 'error' && (
                <p className="slides-error">{slides.error}</p>
              )}
              {slidesEnId === currentAnalysis.analysisId && slidesEnStatus === 'error' && (
                <p className="slides-error">{slidesEn.error}</p>
              )}
              {slidesId === currentAnalysis.analysisId && slidesStatus === 'completed'
                && slides.note && (
                <p className="slides-note">{slides.note}</p>
              )}

              {showAnalysis && currentAnalysis.result && (
                <div className="analysis-result r2">
                  <AnalysisView
                    result={currentAnalysis.result}
                    problemkartaUrl={problemkartaUrl}
                    onOpenPage={(page) => openDocPage(currentAnalysis.analysisId,
                      getDisplayFilenames(currentAnalysis), page)}
                  />
                </div>
              )}

              {currentAnalysis.corrections && (
                <details className="corrections-panel r2">
                  <summary data-tip="dp.corrections">Rättelser och uppdateringar</summary>
                  {currentAnalysis.rerunCompletedAt && (
                    <p className="corrections-applied">
                      Analysen kördes om {formatDate(`${currentAnalysis.rerunCompletedAt}Z`)}
                      {currentAnalysis.correctionsApplied?.length
                        ? ` med ${currentAnalysis.correctionsApplied.join(', ')}.`
                        : ' utan rättelser.'}
                    </p>
                  )}
                  <div className="corrections-md">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {currentAnalysis.corrections}
                    </ReactMarkdown>
                  </div>
                </details>
              )}

              <div className="chat-section">
                <h4>Fråga dokumentet eller lämna en rättelse</h4>
                <DPChat
                  analysisId={currentAnalysis.analysisId}
                  onOpenPage={(page) => openDocPage(currentAnalysis.analysisId,
                    getDisplayFilenames(currentAnalysis), page)}
                  onCorrections={(md) => {
                    const id = currentAnalysis.analysisId;
                    setCurrentAnalysis((a) => (a && a.analysisId === id
                      ? { ...a, corrections: md } : a));
                    // the decks are now outdated - show it on the buttons
                    fetchSlides(id);
                    fetchSlides(id, 'en');
                  }}
                  // picks up rerunStatus, which starts the status polling
                  onRerun={() => checkAnalysisStatus(currentAnalysis.analysisId)}
                />
              </div>
            </>
          )}

          {currentAnalysis.status === 'error' && (
            <p className="error-text">
              {currentAnalysis.errorMessage || 'Ett fel uppstod under analysen'}
            </p>
          )}
        </div>
      )}

      {/* Analysis History */}
      {analyses.length > 0 && (
        <div className="analysis-history">
          <h3>Tidigare analyser</h3>
          <div className="history-list">
            {analyses.map(analysis => {
              const done = analysis.status === 'completed';
              const open = done && expandedId === analysis.analysisId;
              const toggle = () => setExpandedId(open ? null : analysis.analysisId);
              const info = (
                <div className="history-info">
                  <span className="history-date">
                    {formatDate(analysis.timestamp)}
                  </span>
                  <span className="history-files">
                    {getDisplayFilenames(analysis)}
                  </span>
                  {analysis.kommun && (
                    <span className="history-kommun">{analysis.kommun}</span>
                  )}
                  {analysis.numPages && (
                    <span className="history-pages">{analysis.numPages} sidor</span>
                  )}
                </div>
              );
              return (
                <div key={analysis.analysisId}
                     className={`history-item ${getStatusClass(analysis.status)} ${open ? 'expanded' : ''}`}>
                  {done ? (
                    // Completed rows expand to their actions instead of
                    // showing a "Klar" badge and a single Word icon.
                    <div
                      className="history-head"
                      role="button"
                      tabIndex={0}
                      aria-expanded={open}
                      onClick={toggle}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
                      }}
                      data-tip={open ? 'dp.historyCollapse' : 'dp.historyExpand'}
                    >
                      {info}
                      <span className="history-chevron" aria-hidden="true">{open ? '▾' : '▸'}</span>
                    </div>
                  ) : (
                    <div className="history-head">
                      {info}
                      <span className={`status-badge ${getStatusClass(analysis.status)}`}>
                        {getStatusText(analysis.status)}
                      </span>
                    </div>
                  )}
                  {open && (
                    <div className="history-details">
                      {analysis.planNamn && (
                        <p className="history-plan">{analysis.planNamn}</p>
                      )}
                      <div className="history-detail-actions">
                        <button
                          onClick={() => openAnalysis(analysis.analysisId)}
                          className="history-btn"
                          data-tip="dp.openAnalysis"
                        >
                          Öppna analysen
                        </button>
                        <button
                          onClick={() => downloadWord(analysis.analysisId)}
                          className="history-btn"
                          data-tip="dp.historyWord"
                        >
                          📝 Word
                        </button>
                        {['completed', 'outdated'].includes(analysis.slidesStatus) && (
                          <button
                            onClick={() => downloadSlides(analysis.analysisId)}
                            className="history-btn"
                            data-tip="dp.historySlides"
                          >
                            📊 Beslutsbilder
                          </button>
                        )}
                        {['completed', 'outdated'].includes(analysis.slidesStatusEn) && (
                          <button
                            onClick={() => downloadSlides(analysis.analysisId, 'en')}
                            className="history-btn"
                            data-tip="dp.historySlidesEn"
                          >
                            📊 English
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      </div>

      {viewer && (
        <DocViewerModal
          file={{ url: viewer.url, filename: viewer.filename }}
          initialPage={viewer.page}
          onClose={() => setViewer(null)}
        />
      )}
    </>
  );
};

export default DPAnalyzer;
